import { AnnotationId, type DocumentId, StructuredAnchor } from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Schema } from "effect";

// The collab surface's manual-annotation half of the REST client (ADR-0045, U6/U13 follow-up):
// `POST /api/annotate-structured` (`{ documentId, anchor, comment }`, `anchor` being the
// selection's exact `{ from, to, atVersion }`, D12). This is the `annotate-action.ts` sibling for
// the CM host — there is no `Locator` to build (the CM client already has the exact offsets) and
// no scope picker (see `selection-annotate.tsx`'s module doc for the disclosed v1 scope). A 404/422
// is the server refusing the write for a caller-fixable reason: the anchor's version doesn't
// exist, or its offsets ran out of the document's bounds at that version (the selection went stale
// between the user picking it and submitting) — surfaced as a hint, not a swallowed rejection.

const AnnotateStructuredSuccess = Schema.Struct({
  status: Schema.Literal("ok"),
  annotationId: AnnotationId,
  anchor: StructuredAnchor,
});

// The server's 4xx body (`ResolveNotFound` / `ResolveUnprocessable`): `kind` is the original op
// error's `_tag`.
const AnnotateStructuredRejectedBody = Schema.Struct({
  kind: Schema.String,
  message: Schema.String,
});

export class AnnotateStructuredRejected extends Data.TaggedError("AnnotateStructuredRejected")<{
  readonly kind: string;
  readonly message: string;
}> {}

const hintFor = (kind: string): string => {
  switch (kind) {
    case "StaleAnchor":
      return "The document changed since you selected that text — reselect and try again.";
    case "DocumentNotFound":
      return "Couldn't find that document version — reload the page and try again.";
    default:
      return "Couldn't raise that finding — reselect and try again.";
  }
};

export interface StructuredAnchorInput {
  readonly from: number;
  readonly to: number;
  readonly atVersion: number;
}

export const annotateStructured = (
  documentId: DocumentId,
  anchor: StructuredAnchorInput,
  comment: string,
) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.post("/api/annotate-structured").pipe(
        HttpClientRequest.bodyUnsafeJson({ documentId, anchor, comment }),
      ),
    );
    if (response.status === 404 || response.status === 422) {
      const kind = yield* HttpClientResponse.schemaBodyJson(AnnotateStructuredRejectedBody)(
        response,
      ).pipe(
        Effect.map((body) => body.kind),
        Effect.orElseSucceed(() => "Unknown"),
      );
      return yield* Effect.fail(new AnnotateStructuredRejected({ kind, message: hintFor(kind) }));
    }
    return yield* HttpClientResponse.schemaBodyJson(AnnotateStructuredSuccess)(response);
  }).pipe(Effect.scoped);
